import type { AfterViewInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxCoordinates, OnyxCustomAddress } from '@onyx/angular';
import {
  OnyxAddressInputComponent,
  OnyxAutoFocusDirective,
  OnyxFormMode,
  OnyxIconComponent,
  OnyxMapComponent,
  OnyxMapEvent,
  OnyxMapMarkerType,
  OnyxMessageComponent,
  OnyxSectionComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { isEqual } from 'lodash';
import { distinctUntilChanged, map, startWith } from 'rxjs';

type InformationFormGroup = ReturnType<
  (typeof InformationFormComponent)['buildForm']
>;

@Component({
  selector: 'app-information-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxAddressInputComponent,
    OnyxTextFieldComponent,
    OnyxMapComponent,
    OnyxAutoFocusDirective,
    OnyxMessageComponent,
    OnyxIconComponent,
    TranslatePipe,
  ],
  templateUrl: './information-form.component.html',
  styleUrl: './information-form.component.scss',
})
export class InformationFormComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = input.required<InformationFormGroup>();
  public readonly mode = input.required<OnyxFormMode>();
  public readonly heading = input.required<string>();
  public readonly name = input.required<string>();
  public readonly markerType = input.required<OnyxMapMarkerType>();

  protected readonly I18N = 'forms.information';

  private readonly map = viewChild.required(OnyxMapComponent);

  protected showNewAddressMessage = computed(
    () =>
      this.markerType() === OnyxMapMarkerType.POINT_OF_INTEREST &&
      this.mode() === OnyxFormMode.EDIT,
  );

  public ngAfterViewInit(): void {
    this.form()
      .controls.address.valueChanges.pipe(
        startWith(this.form().controls.address.value),
        map((address) => address?.coordinates),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((coordinates: OnyxCoordinates | undefined) => {
        const MARKER_ID = 'marker';
        if (coordinates) {
          this.map().dispatch(
            new OnyxMapEvent.AddUpdateMarker({
              id: MARKER_ID,
              type: this.markerType(),
              coordinates,
            }),
          );
        } else {
          this.map().dispatch(new OnyxMapEvent.RemoveMarker({ id: MARKER_ID }));
        }
        this.map().dispatch(new OnyxMapEvent.FitContent());
      });
  }

  protected setName(name: string): void {
    const nameControl = this.form().controls.name;
    if (!nameControl.value) nameControl.setValue(name);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      name: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      address: fb.control<OnyxCustomAddress | null>(null, [
        Validators.required,
      ]),
    });
  }
}
